package ds.pms.tokafull.adminweb

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import org.w3c.files.File

/** Lowercase UUID strings so <select> option values match IDs from listings / JDBC. */
private fun normalizeAdminUuid(s: String): String = s.removePrefix("\uFEFF").trim().lowercase()

/** Same normalization used for bulk selection in the product table and API calls. */
internal fun canonicalBulkProductId(raw: String): String = normalizeAdminUuid(raw)

private fun Set<String>.normalizedBulkSelectionSet(): Set<String> =
    map { normalizeAdminUuid(it) }.filter { it.isNotBlank() }.toSet()

enum class AdminTab { Products, Variants, Matrix, Modifiers, Orders, Users }

/** Value to label for order status dropdowns (matches server `CatalogRepository.normalizeAdminOrderStatus`). */
val ADMIN_ORDER_STATUS_OPTIONS: List<Pair<String, String>> = listOf(
    "placed" to "New order",
    "confirmed" to "Confirmed",
    "processing" to "Processing",
    "shipped" to "Shipped",
    "delivered" to "Delivered",
    "cancelled" to "Cancelled"
)
enum class AdjustmentType { Absolute, Percentage }

data class BulkAdjustmentState(
    val brandCode: String = "ST",
    val type: AdjustmentType = AdjustmentType.Absolute,
    val amount: String = ""
)

/** Draft for pricing matrix row editor (modal). Empty string = leave brand unchanged on save. */
data class MatrixRowEditDraft(
    val productId: String,
    val productName: String,
    val productNameUr: String? = null,
    val baseSku: String,
    val st: String,
    val c: String,
    val d: String,
    val gen: String,
    val n: String,
    val s: String,
    val t: String
)

data class CreateProductForm(
    val productLineId: String = "",
    val catalogSectionId: String = "",
    val name: String = "",
    val nameUr: String = "",
    val selectedCastIds: List<String> = emptyList(),
    val selectedBrandCodes: List<String> = emptyList(),
    val selectedWheelSizes: List<String> = emptyList(),
    val selectedGrades: List<String> = emptyList(),
    val selectedSizes: List<String> = emptyList(),
    val selectedMechanisms: List<String> = emptyList(),
    val basePrice: String = "",
    val costPrice: String = "",
    val imageUrl: String? = null,
    val pendingImageFile: File? = null,
    val clearImage: Boolean = false,
)

data class EditProductForm(
    val productId: String = "",
    val productLineId: String = "",
    val catalogSectionId: String = "",
    val name: String = "",
    val nameUr: String = "",
    val imageUrl: String? = null,
    val pendingImageFile: File? = null,
    val clearImage: Boolean = false,
    val selectedCastIds: List<String> = emptyList(),
    val selectedBrandCodes: List<String> = emptyList(),
    val selectedWheelSizes: List<String> = emptyList(),
    val selectedGrades: List<String> = emptyList(),
    val selectedSizes: List<String> = emptyList(),
    val selectedMechanisms: List<String> = emptyList(),
)

data class ModifierForm(
    val attrKey: String = "",
    val attrValue: String = "",
    val attrValueUr: String = "",
    val priceImpact: String = ""
)

data class UpdateModifierForm(
    val modifierId: String = "",
    val attrKey: String = "",
    val attrValue: String = "",
    val attrValueUr: String = "",
    val priceImpact: String = ""
)

data class AdminUiState(
    val authReady: Boolean = false,
    val signedIn: Boolean = false,
    val sessionUsername: String? = null,
    val tab: AdminTab = AdminTab.Products,
    val loading: Boolean = false,
    val saving: Boolean = false,
    val error: String? = null,
    val matrix: List<PricingMatrixRowDto> = emptyList(),
    val matrixTotal: Long = 0,
    val matrixPage: Int = 1,
    val matrixPageSize: Int = 25,
    val products: List<AdminProductSummaryDto> = emptyList(),
    val productsTotal: Long = 0,
    val productsPage: Int = 1,
    val productsPageSize: Int = 25,
    val modifiers: List<ModifierRowDto> = emptyList(),
    val modifiersTotal: Long = 0,
    val modifiersPage: Int = 1,
    val modifiersPageSize: Int = 25,
    /** Search text for Price modifiers list (attr key/value/impact/id). */
    val modifiersSearchQuery: String = "",
    /** True when GET /admin/modifiers returned HTTP 404 (rebuild/restart API from this repo). */
    val modifiersEndpointMissing: Boolean = false,
    /** Set when a table row’s Edit is used; drives row highlight and clears on Cancel / successful save. */
    val editingModifierId: String? = null,
    /** True when GET /admin/users returned HTTP 404 (rebuild/restart API from this repo). */
    val usersEndpointMissing: Boolean = false,
    val users: List<AdminUserRowDto> = emptyList(),
    val usersTotal: Long = 0,
    val usersPage: Int = 1,
    val usersPageSize: Int = 25,
    val orders: List<AdminOrderSummaryDto> = emptyList(),
    val ordersTotal: Long = 0,
    val ordersPage: Int = 1,
    val ordersPageSize: Int = 25,
    /** Empty = all statuses. Values match server status codes (e.g. placed, confirmed). */
    val ordersStatusFilter: String = "",
    /** Variants search page (separate from Products catalogue search). */
    val variantSearchQuery: String = "",
    val variantSearchResults: List<AdminVariantSearchRowDto> = emptyList(),
    val variantSearchTotal: Long = 0,
    val variantSearchPage: Int = 1,
    val variantSearchPageSize: Int = 25,
    val variantSearchHasQueried: Boolean = false,
    /** When non-null, order detail panel is shown (view + print). */
    val orderDetail: OrderDetailDto? = null,
    val newUserForm: NewUserFormState = NewUserFormState(),
    val editUserForm: EditUserFormState? = null,
    val variants: List<VariantDetailDto> = emptyList(),
    val variantPage: Int = 1,
    val variantPageSize: Int = 15,
    val selectedProductId: String? = null,
    val searchQuery: String = "",
    val filterSize: String = "",
    val filterMechanism: String = "",
    val filterGrade: String = "",
    val filterBrand: String = "",
    val filterCast: String = "",
    val showCreateProductModal: Boolean = false,
    val createProductLoading: Boolean = false,
    val showEditProductModal: Boolean = false,
    val editProductLoading: Boolean = false,
    val editProductForm: EditProductForm = EditProductForm(),
    val productLinesOptions: List<ProductLineDto> = emptyList(),
    val catalogSectionsForCreate: List<CatalogSectionDto> = emptyList(),
    val sizePickerOptions: List<String> = emptyList(),
    val mechanismPickerOptions: List<String> = emptyList(),
    val gradePickerOptions: List<String> = emptyList(),
    val wheelPickerOptions: List<String> = emptyList(),
    val castOptionsForCreate: List<AdminCastOptionDto> = emptyList(),
    /** True while POST /admin/cast is in flight from the create-product dialog. */
    val castCreateBusy: Boolean = false,
    /** Bumped after a cast is saved from the dialog so draft fields reset. */
    val inlineCastFormNonce: Int = 0,
    val brandOptionsForCreate: List<AdminBrandOptionDto> = emptyList(),
    val brandOptionsForUsers: List<AdminBrandOptionDto> = emptyList(),
    val createProductForm: CreateProductForm = CreateProductForm(),
    val bulkAdjustment: BulkAdjustmentState = BulkAdjustmentState(),
    val matrixEditDraft: MatrixRowEditDraft? = null,
    val createModifierForm: ModifierForm = ModifierForm(),
    val updateModifierForm: UpdateModifierForm = UpdateModifierForm(),
    val auditLog: List<String> = emptyList(),
    /**
     * Product IDs selected for bulk actions (normalized lowercase UUID strings).
     * Accumulates across pages until cleared, search applied, or bulk action completes.
     */
    val selectedProductIdsForBulk: Set<String> = emptySet(),
)

class AdminViewModel(
    private val api: AdminApiClient = AdminApiClient()
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(AdminUiState(loading = true))
    val uiState: StateFlow<AdminUiState> = _uiState.asStateFlow()

    /** Debounced live search jobs (cancelled on each keystroke / clear). */
    private var productSearchJob: Job? = null
    private var variantSearchJob: Job? = null
    private var productSearchSeq: Int = 0
    private var variantSearchSeq: Int = 0

    private companion object {
        const val LIVE_SEARCH_DEBOUNCE_MS = 300L
    }

    private fun CatalogSectionDto.isTokaSparePartsSection(): Boolean {
        val key = listOf(code, name.orEmpty())
            .joinToString(" ")
            .trim()
            .lowercase()
            .replace("_", " ")
            .replace("-", " ")
        return "spare parts" in key
    }

    private fun isCreateBrandOptionalForSection(f: CreateProductForm, sections: List<CatalogSectionDto>): Boolean =
        sections.firstOrNull { it.id.equals(f.catalogSectionId, ignoreCase = true) }
            ?.isTokaSparePartsSection()
            ?: false

    private fun isCreateCastOptionalForSection(f: CreateProductForm, sections: List<CatalogSectionDto>): Boolean =
        isCreateBrandOptionalForSection(f, sections)

    private fun mergeDistinctSortedPickerOptions(options: List<String>, newValue: String): List<String> {
        val v = newValue.trim()
        if (v.isBlank()) return options
        if (options.any { it.equals(v, ignoreCase = true) }) return options
        return (options + v).sortedWith(compareBy { it.lowercase() })
    }

    /** Ensures typed picker values appear in Modifiers (`attribute_modifiers`, zero price delta). Best-effort. */
    private fun registerPickerModifierAsync(attrKey: String, attrValue: String) {
        val v = attrValue.trim()
        if (v.isBlank()) return
        scope.launch {
            runCatching {
                api.registerPickerValue(RegisterPickerValueRequestDto(attrKey = attrKey, attrValue = v))
            }
        }
    }

    /** Prefer values from modifiers; if none match yet, merge variant/specs sources so selectors stay usable. */
    private suspend fun pickerSuggestions(attrKey: String): List<String> {
        val fromMods =
            api.getPickerValues(attrKey, modifiersOnly = true).map { it.trim() }.filter { it.isNotEmpty() }.distinct()
        if (fromMods.isNotEmpty()) {
            return fromMods.sortedWith(compareBy { it.lowercase() })
        }
        return api.getPickerValues(attrKey, modifiersOnly = false).map { it.trim() }.filter { it.isNotEmpty() }.distinct()
            .sortedWith(compareBy { it.lowercase() })
    }

    fun bootstrap() {
        scope.launch {
            val u = AdminSession.getUsername()
            if (!AdminSession.isSignedIn()) {
                _uiState.value = AdminUiState(authReady = true, signedIn = false, loading = false, sessionUsername = u)
                return@launch
            }
            _uiState.value = _uiState.value.copy(
                signedIn = true,
                authReady = true,
                sessionUsername = u ?: AdminSession.getUsername(),
                loading = true,
                error = null
            )
            val ok = runCatching { loadProductsAndMatrixInternal() }
            if (ok.isFailure) {
                AdminSession.clear()
                _uiState.value = AdminUiState(authReady = true, signedIn = false, loading = false)
            }
        }
    }

    fun login(username: String, password: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            val auth = runCatching { api.login(username, password) }
            if (auth.isFailure) {
                val err = auth.exceptionOrNull()
                val msg = err?.message.orEmpty()
                val hint = if (
                    msg.contains("Failed to fetch", ignoreCase = true) ||
                    msg.contains("NetworkError", ignoreCase = true) ||
                    msg.contains("Load failed", ignoreCase = true)
                ) {
                    "Cannot reach the API. Check admin-web env-config.js (ADMIN_API_BASE_URL), firewall, and HTTPS. " +
                        "For a local Ktor API use tools\\run-admin-ui.ps1 (or start-dev-stack) and either a full API URL or empty base with the webpack proxy to :8080."
                } else {
                    msg.ifBlank { "Sign-in failed" }
                }
                _uiState.value = _uiState.value.copy(loading = false, error = hint)
                return@launch
            }
            val r = auth.getOrThrow()
            AdminSession.setSession(r.token, r.username)
            _uiState.value = _uiState.value.copy(
                signedIn = true,
                sessionUsername = r.username,
                loading = true,
                error = null
            )
            val load = runCatching { loadProductsAndMatrixInternal() }
            if (load.isFailure) {
                _uiState.value = _uiState.value.copy(loading = false, error = load.exceptionOrNull()?.message ?: "Load failed")
            }
        }
    }

    fun logout() {
        scope.launch {
            api.logout()
            _uiState.value = AdminUiState(authReady = true, signedIn = false, loading = false)
        }
    }

    private suspend fun loadProductsAndMatrixInternal() {
        val s = _uiState.value
        val p = api.getProductsPage(
            page = s.productsPage,
            pageSize = s.productsPageSize,
            q = s.searchQuery.takeIf { it.isNotBlank() },
            size = s.filterSize.takeIf { it.isNotBlank() },
            mechanism = s.filterMechanism.takeIf { it.isNotBlank() },
            grade = s.filterGrade.takeIf { it.isNotBlank() },
            brand = s.filterBrand.takeIf { it.isNotBlank() },
            cast = s.filterCast.takeIf { it.isNotBlank() },
        )
        val m = api.getMatrixPage(s.matrixPage, s.matrixPageSize)
        _uiState.value = _uiState.value.copy(
            loading = false,
            products = p.items,
            productsTotal = p.total,
            productsPage = p.page,
            matrix = m.items,
            matrixTotal = m.total,
            matrixPage = m.page
        )
    }

    fun refreshAll() {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            runCatching { loadProductsAndMatrixInternal() }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Load failed")
                }
        }
    }

    fun loadProductsPage(page: Int) {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(loading = true, error = null, productsPage = page.coerceAtLeast(1))
            runCatching {
                val s = _uiState.value
                api.getProductsPage(
                    page = s.productsPage,
                    pageSize = s.productsPageSize,
                    q = s.searchQuery.takeIf { it.isNotBlank() },
                    size = s.filterSize.takeIf { it.isNotBlank() },
                    mechanism = s.filterMechanism.takeIf { it.isNotBlank() },
                    grade = s.filterGrade.takeIf { it.isNotBlank() },
                    brand = s.filterBrand.takeIf { it.isNotBlank() },
                    cast = s.filterCast.takeIf { it.isNotBlank() },
                )
            }.onSuccess { p ->
                _uiState.value = _uiState.value.copy(loading = false, products = p.items, productsTotal = p.total, productsPage = p.page)
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed to load products")
            }
        }
    }

    fun applyProductSearch() {
        productSearchJob?.cancel()
        _uiState.value = _uiState.value.copy(selectedProductIdsForBulk = emptySet())
        loadProductsPage(1)
    }

    fun clearProductSearch() {
        productSearchJob?.cancel()
        _uiState.value = _uiState.value.copy(
            searchQuery = "",
            selectedProductIdsForBulk = emptySet(),
        )
        loadProductsPage(1)
    }

    fun toggleProductBulkSelection(productId: String) {
        val id = normalizeAdminUuid(productId)
        if (id.isBlank()) return
        val cur = _uiState.value.selectedProductIdsForBulk.normalizedBulkSelectionSet()
        _uiState.value = _uiState.value.copy(
            selectedProductIdsForBulk = (if (id in cur) cur - id else cur + id).normalizedBulkSelectionSet(),
        )
    }

    /** Adds every product on the current listing page to the bulk selection (keeps prior pages’ picks). */
    fun selectAllProductsOnCurrentPage() {
        val snap = _uiState.value.products
        if (snap.isEmpty()) return
        val pageIds = snap.map { normalizeAdminUuid(it.productId) }.filter { it.isNotBlank() }.toSet()
        if (pageIds.isEmpty()) return
        val s = _uiState.value
        val merged = s.selectedProductIdsForBulk.normalizedBulkSelectionSet() + pageIds
        _uiState.value = s.copy(selectedProductIdsForBulk = merged.normalizedBulkSelectionSet())
    }

    /** Removes only the current page’s products from the bulk selection. */
    fun deselectAllProductsOnCurrentPage() {
        val snap = _uiState.value.products
        val pageIds = snap.map { normalizeAdminUuid(it.productId) }.filter { it.isNotBlank() }.toSet()
        if (pageIds.isEmpty()) return
        val s = _uiState.value
        _uiState.value = s.copy(
            selectedProductIdsForBulk = (s.selectedProductIdsForBulk.normalizedBulkSelectionSet() - pageIds)
                .normalizedBulkSelectionSet(),
        )
    }

    fun clearBulkProductSelection() {
        _uiState.value = _uiState.value.copy(selectedProductIdsForBulk = emptySet())
    }

    fun bulkActivateSelected() {
        val ids = _uiState.value.selectedProductIdsForBulk.toList()
        if (ids.isEmpty()) return
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val failures = mutableListOf<String>()
            for (id in ids) {
                runCatching { api.activateProduct(id) }.onFailure { failures += id }
            }
            val ok = ids.size - failures.size
            addLog("Bulk activate: $ok succeeded, ${failures.size} failed")
            _uiState.value = _uiState.value.copy(
                saving = false,
                selectedProductIdsForBulk = emptySet(),
                error = if (failures.isEmpty()) null else "Activate failed for ${failures.size} product(s). First IDs: ${failures.take(5).joinToString()}",
            )
            loadProductsPage(_uiState.value.productsPage)
        }
    }

    fun bulkArchiveSelected() {
        val ids = _uiState.value.selectedProductIdsForBulk.toList()
        if (ids.isEmpty()) return
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val failures = mutableListOf<String>()
            for (id in ids) {
                runCatching { api.archiveProduct(id) }.onFailure { failures += id }
            }
            val ok = ids.size - failures.size
            addLog("Bulk deactivate (archive): $ok succeeded, ${failures.size} failed")
            _uiState.value = _uiState.value.copy(
                saving = false,
                selectedProductIdsForBulk = emptySet(),
                error = if (failures.isEmpty()) null else "Deactivate failed for ${failures.size} product(s). First IDs: ${failures.take(5).joinToString()}",
            )
            loadProductsPage(_uiState.value.productsPage)
        }
    }

    fun loadMatrixPage(page: Int) {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(loading = true, error = null, matrixPage = page.coerceAtLeast(1))
            runCatching {
                val s = _uiState.value
                api.getMatrixPage(s.matrixPage, s.matrixPageSize)
            }.onSuccess { m ->
                _uiState.value = _uiState.value.copy(loading = false, matrix = m.items, matrixTotal = m.total, matrixPage = m.page)
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed to load matrix")
            }
        }
    }

    fun loadModifiersPage(page: Int) {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(
                loading = true,
                error = null,
                modifiersPage = page.coerceAtLeast(1),
                modifiersEndpointMissing = false
            )
            runCatching {
                val s = _uiState.value
                api.getModifiersPage(
                    s.modifiersPage,
                    s.modifiersPageSize,
                    s.modifiersSearchQuery.trim().takeIf { it.isNotEmpty() },
                )
            }.onSuccess { load ->
                val r = load.page
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    modifiers = r.items,
                    modifiersTotal = r.total,
                    modifiersPage = r.page,
                    modifiersEndpointMissing = load.endpointMissing
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed to load modifiers")
            }
        }
    }

    fun setModifiersSearch(query: String) {
        _uiState.value = _uiState.value.copy(modifiersSearchQuery = query)
    }

    fun applyModifiersSearch() {
        loadModifiersPage(1)
    }

    fun clearModifiersSearch() {
        _uiState.value = _uiState.value.copy(modifiersSearchQuery = "")
        loadModifiersPage(1)
    }

    fun loadUsersPage(page: Int) {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(
                loading = true,
                error = null,
                usersPage = page.coerceAtLeast(1),
                usersEndpointMissing = false
            )
            val brandsResult = runCatching { api.getBrandOptions() }
            runCatching {
                val s = _uiState.value
                api.getUsersPage(s.usersPage, s.usersPageSize)
            }.onSuccess { load ->
                val r = load.page
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    users = r.items,
                    usersTotal = r.total,
                    usersPage = r.page,
                    usersEndpointMissing = load.endpointMissing,
                    brandOptionsForUsers = brandsResult.getOrDefault(emptyList()),
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed to load users")
            }
        }
    }

    fun updateNewUserForm(transform: (NewUserFormState) -> NewUserFormState) {
        val s = _uiState.value
        _uiState.value = s.copy(newUserForm = transform(s.newUserForm))
    }

    fun openUserEditor(u: AdminUserRowDto) {
        _uiState.value = _uiState.value.copy(
            editUserForm = EditUserFormState(
                userId = u.id,
                username = u.username,
                userType = u.userType.lowercase(),
                status = u.status.lowercase(),
                nameEn = u.nameEn.orEmpty(),
                nameUr = u.nameUr.orEmpty(),
                addressEn = u.addressEn.orEmpty(),
                mobileEn = u.mobileEn.orEmpty(),
                passwordNew = "",
                selectedBrandCodes = u.brandCodes,
            ),
            error = null,
        )
    }

    fun updateEditUserForm(transform: (EditUserFormState) -> EditUserFormState) {
        val s = _uiState.value
        val cur = s.editUserForm ?: return
        _uiState.value = s.copy(editUserForm = transform(cur))
    }

    fun addNewUserBrand(code: String) = updateNewUserForm { f ->
        if (code in f.selectedBrandCodes) f else f.copy(selectedBrandCodes = f.selectedBrandCodes + code)
    }

    fun removeNewUserBrand(code: String) = updateNewUserForm { f ->
        f.copy(selectedBrandCodes = f.selectedBrandCodes - code)
    }

    fun addEditUserBrand(code: String) = updateEditUserForm { f ->
        if (code in f.selectedBrandCodes) f else f.copy(selectedBrandCodes = f.selectedBrandCodes + code)
    }

    fun removeEditUserBrand(code: String) = updateEditUserForm { f ->
        f.copy(selectedBrandCodes = f.selectedBrandCodes - code)
    }

    fun closeUserEditor() {
        _uiState.value = _uiState.value.copy(editUserForm = null)
    }

    fun saveUserEditor() {
        val s = _uiState.value
        val f = s.editUserForm ?: return
        if (f.userType == "agent") {
            if (f.nameEn.isBlank() || f.addressEn.isBlank() || f.mobileEn.isBlank()) {
                _uiState.value = s.copy(error = "App users require name, address, and mobile.")
                return
            }
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val req = PatchUserRequestDto(
                nameEn = f.nameEn,
                nameUr = f.nameUr,
                addressEn = f.addressEn,
                mobileEn = f.mobileEn,
                userType = f.userType,
                status = f.status,
                password = f.passwordNew.trim().takeIf { it.isNotEmpty() },
                brandCodes = f.selectedBrandCodes,
            )
            runCatching { api.patchUser(f.userId, req) }
                .onSuccess {
                    addLog("Updated user ${f.username}")
                    _uiState.value = _uiState.value.copy(saving = false, editUserForm = null)
                    loadUsersPage(_uiState.value.usersPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Update user failed")
                }
        }
    }

    fun patchUserStatus(userId: String, status: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.patchUser(userId, PatchUserRequestDto(status = status.trim().lowercase())) }
                .onSuccess {
                    _uiState.value = _uiState.value.copy(saving = false)
                    loadUsersPage(_uiState.value.usersPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Status update failed")
                }
        }
    }

    fun createUser() {
        val s = _uiState.value
        val f = s.newUserForm
        if (f.username.isBlank() || f.password.isBlank()) {
            _uiState.value = s.copy(error = "Username and password are required")
            return
        }
        if (f.userType == "agent") {
            if (f.nameEn.isBlank() || f.addressEn.isBlank() || f.mobileEn.isBlank()) {
                _uiState.value = s.copy(error = "App users require name, address, and mobile.")
                return
            }
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val req = CreateUserRequestDto(
                username = f.username.trim(),
                password = f.password,
                userType = f.userType.trim().lowercase(),
                status = f.status.trim().lowercase(),
                nameEn = f.nameEn.trim().takeIf { it.isNotEmpty() },
                nameUr = f.nameUr.trim().takeIf { it.isNotEmpty() },
                addressEn = f.addressEn.trim().takeIf { it.isNotEmpty() },
                mobileEn = f.mobileEn.trim().takeIf { it.isNotEmpty() },
                brandCodes = f.selectedBrandCodes,
            )
            runCatching { api.createUser(req) }
                .onSuccess {
                    addLog("Created user ${f.username.trim()}")
                    _uiState.value = _uiState.value.copy(saving = false, newUserForm = NewUserFormState(), usersPage = 1)
                    loadUsersPage(1)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Create user failed")
                }
        }
    }

    fun deleteUser(userId: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.deleteUser(userId) }
                .onSuccess {
                    addLog("Deleted user $userId")
                    _uiState.value = _uiState.value.copy(saving = false)
                    loadUsersPage(_uiState.value.usersPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Delete user failed")
                }
        }
    }

    fun setTab(tab: AdminTab) {
        _uiState.value = _uiState.value.copy(tab = tab, error = null, orderDetail = null)
        when (tab) {
            AdminTab.Modifiers -> loadModifiersPage(1)
            AdminTab.Orders -> loadOrdersPage(1)
            AdminTab.Users -> loadUsersPage(1)
            AdminTab.Variants -> searchVariantsPage(1)
            else -> Unit
        }
    }

    fun setVariantSearchQuery(query: String) {
        _uiState.value = _uiState.value.copy(variantSearchQuery = query)
        variantSearchJob?.cancel()
        val seq = ++variantSearchSeq
        variantSearchJob = scope.launch {
            delay(LIVE_SEARCH_DEBOUNCE_MS)
            if (seq != variantSearchSeq) return@launch
            searchVariantsPage(1)
        }
    }

    fun clearVariantSearch() {
        variantSearchJob?.cancel()
        _uiState.value = _uiState.value.copy(variantSearchQuery = "")
        searchVariantsPage(1)
    }

    fun searchVariantsPage(page: Int = 1) {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            val q = _uiState.value.variantSearchQuery.trim()
            _uiState.value = _uiState.value.copy(
                loading = true,
                error = null,
                variantSearchPage = page.coerceAtLeast(1),
                variantSearchHasQueried = true,
            )
            runCatching {
                val s = _uiState.value
                api.searchVariantsPage(
                    s.variantSearchPage,
                    s.variantSearchPageSize,
                    q.takeIf { it.isNotEmpty() },
                )
            }.onSuccess { r ->
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    variantSearchResults = r.items,
                    variantSearchTotal = r.total,
                    variantSearchPage = r.page,
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message ?: "Variant search failed",
                )
            }
        }
    }

    /** Jump to Products tab and open that product's variants panel (existing behaviour). */
    fun openProductFromVariantSearch(productId: String) {
        _uiState.value = _uiState.value.copy(tab = AdminTab.Products, error = null, orderDetail = null)
        openVariants(productId)
    }

    fun loadOrdersPage(page: Int) {
        scope.launch {
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(loading = true, error = null, ordersPage = page.coerceAtLeast(1))
            runCatching {
                val s = _uiState.value
                api.getOrdersPage(
                    page = s.ordersPage,
                    pageSize = s.ordersPageSize,
                    status = s.ordersStatusFilter.takeIf { it.isNotBlank() },
                )
            }.onSuccess { r ->
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    orders = r.items,
                    ordersTotal = r.total,
                    ordersPage = r.page
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed to load orders")
            }
        }
    }

    fun setOrdersStatusFilter(status: String) {
        val next = status.trim().lowercase()
        _uiState.value = _uiState.value.copy(ordersStatusFilter = next, ordersPage = 1)
        loadOrdersPage(1)
    }

    fun openOrderDetail(orderCode: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            runCatching { api.getAdminOrderDetail(orderCode) }
                .onSuccess { d ->
                    _uiState.value = _uiState.value.copy(loading = false, orderDetail = d)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed to load order")
                }
        }
    }

    fun closeOrderDetail() {
        _uiState.value = _uiState.value.copy(orderDetail = null)
    }

    fun updateOrderStatus(orderCode: String, newStatus: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.patchOrderStatus(orderCode, newStatus) }
                .onSuccess {
                    addLog("Updated order $orderCode → $newStatus")
                    // Optimistic local update so the dropdown keeps the new value immediately.
                    val normalized = newStatus.trim().lowercase()
                    _uiState.value = _uiState.value.copy(
                        orders = _uiState.value.orders.map { o ->
                            if (o.orderId == orderCode) o.copy(status = normalized) else o
                        },
                        orderDetail = _uiState.value.orderDetail?.let { d ->
                            if (d.orderId == orderCode) d.copy(status = normalized) else d
                        },
                    )
                    val page = _uiState.value.ordersPage
                    val ps = _uiState.value.ordersPageSize
                    val refreshDetail = _uiState.value.orderDetail?.orderId == orderCode
                    runCatching {
                        api.getOrdersPage(
                            page = page,
                            pageSize = ps,
                            status = _uiState.value.ordersStatusFilter.takeIf { it.isNotBlank() },
                        )
                    }
                        .onSuccess { r ->
                            _uiState.value = _uiState.value.copy(
                                saving = false,
                                orders = r.items,
                                ordersTotal = r.total,
                                ordersPage = r.page
                            )
                        }
                        .onFailure { e ->
                            _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Reload orders failed")
                        }
                    if (refreshDetail && _uiState.value.error == null) {
                        runCatching { api.getAdminOrderDetail(orderCode) }
                            .onSuccess { d ->
                                _uiState.value = _uiState.value.copy(orderDetail = d)
                            }
                    }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Status update failed")
                }
        }
    }

    fun updateOrderEstimatedDelivery(orderCode: String, estimatedDelivery: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val value = estimatedDelivery.trim()
            runCatching { api.patchOrder(orderCode, estimatedDelivery = value) }
                .onSuccess {
                    addLog(
                        if (value.isEmpty()) "Cleared est. delivery for $orderCode"
                        else "Set est. delivery for $orderCode → $value"
                    )
                    runCatching { api.getAdminOrderDetail(orderCode) }
                        .onSuccess { d ->
                            _uiState.value = _uiState.value.copy(saving = false, orderDetail = d)
                        }
                        .onFailure { e ->
                            _uiState.value = _uiState.value.copy(
                                saving = false,
                                error = e.message ?: "Reload order failed"
                            )
                        }
                    val page = _uiState.value.ordersPage
                    val ps = _uiState.value.ordersPageSize
                    runCatching {
                        api.getOrdersPage(
                            page = page,
                            pageSize = ps,
                            status = _uiState.value.ordersStatusFilter.takeIf { it.isNotBlank() },
                        )
                    }
                        .onSuccess { r ->
                            _uiState.value = _uiState.value.copy(
                                orders = r.items,
                                ordersTotal = r.total,
                                ordersPage = r.page
                            )
                        }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(
                        saving = false,
                        error = e.message ?: "Est. delivery update failed"
                    )
                }
        }
    }

    fun markOrderPrintedAfterPrint(orderCode: String, wasReprint: Boolean) {
        scope.launch {
            runCatching { api.markOrderPrinted(orderCode) }
                .onSuccess {
                    addLog(
                        if (wasReprint) "Reprinted order $orderCode"
                        else "Printed order $orderCode"
                    )
                    runCatching { api.getAdminOrderDetail(orderCode) }
                        .onSuccess { d -> _uiState.value = _uiState.value.copy(orderDetail = d) }
                    val page = _uiState.value.ordersPage
                    val ps = _uiState.value.ordersPageSize
                    runCatching {
                        api.getOrdersPage(
                            page = page,
                            pageSize = ps,
                            status = _uiState.value.ordersStatusFilter.takeIf { it.isNotBlank() },
                        )
                    }
                        .onSuccess { r ->
                            _uiState.value = _uiState.value.copy(
                                orders = r.items,
                                ordersTotal = r.total,
                                ordersPage = r.page
                            )
                        }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(
                        error = e.message ?: "Could not save print status"
                    )
                }
        }
    }

    fun setSearch(query: String) {
        _uiState.value = _uiState.value.copy(searchQuery = query)
        productSearchJob?.cancel()
        val seq = ++productSearchSeq
        productSearchJob = scope.launch {
            delay(LIVE_SEARCH_DEBOUNCE_MS)
            if (seq != productSearchSeq) return@launch
            if (!_uiState.value.signedIn) return@launch
            _uiState.value = _uiState.value.copy(selectedProductIdsForBulk = emptySet())
            loadProductsPage(1)
        }
    }

    fun updateProductFilters(
        size: String? = null,
        mechanism: String? = null,
        grade: String? = null,
        brand: String? = null,
        cast: String? = null,
    ) {
        val s = _uiState.value
        _uiState.value = s.copy(
            filterSize = size ?: s.filterSize,
            filterMechanism = mechanism ?: s.filterMechanism,
            filterGrade = grade ?: s.filterGrade,
            filterBrand = brand ?: s.filterBrand,
            filterCast = cast ?: s.filterCast,
        )
    }

    fun clearProductFilters() {
        _uiState.value = _uiState.value.copy(
            filterSize = "",
            filterMechanism = "",
            filterGrade = "",
            filterBrand = "",
            filterCast = "",
            selectedProductIdsForBulk = emptySet(),
        )
        loadProductsPage(1)
    }

    fun setVariantPage(page: Int) {
        _uiState.value = _uiState.value.copy(variantPage = page.coerceAtLeast(1))
    }

    fun toggleCreateProductModal(show: Boolean) {
        if (!show) {
            _uiState.value = _uiState.value.copy(showCreateProductModal = false, castCreateBusy = false)
            return
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(
                showCreateProductModal = true,
                showEditProductModal = false,
                createProductLoading = true,
                castCreateBusy = false,
                error = null,
                createProductForm = CreateProductForm(),
                catalogSectionsForCreate = emptyList()
            )
            runCatching {
                val lines = api.getProductLines()
                val sizes = api.getPickerValues("size")
                val mechs = api.getPickerValues("mechanism")
                val grades = pickerSuggestions("Grade")
                val wheels = pickerSuggestions("Wheel Size")
                val brands = api.getBrandOptions()
                _uiState.value = _uiState.value.copy(
                    createProductLoading = false,
                    productLinesOptions = lines,
                    sizePickerOptions = sizes,
                    mechanismPickerOptions = mechs,
                    gradePickerOptions = grades,
                    wheelPickerOptions = wheels,
                    brandOptionsForCreate = brands,
                    castOptionsForCreate = emptyList(),
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(
                    createProductLoading = false,
                    error = e.message ?: "Failed to load create form"
                )
            }
        }
    }

    fun toggleEditProductModal(show: Boolean) {
        if (!show) {
            _uiState.value = _uiState.value.copy(
                showEditProductModal = false,
                editProductLoading = false,
                editProductForm = EditProductForm(),
                catalogSectionsForCreate = emptyList()
            )
            return
        }
    }

    fun openEditProduct(row: AdminProductSummaryDto) {
        scope.launch {
            val lineIdN = normalizeAdminUuid(row.productLineId)
            val sectionIdN = normalizeAdminUuid(row.catalogSectionId)
            if (lineIdN.isBlank() || sectionIdN.isBlank()) {
                _uiState.value = _uiState.value.copy(
                    error = "This product is missing line/section IDs; fix data in DB or re-save from a fresh listing."
                )
                return@launch
            }
            _uiState.value = _uiState.value.copy(
                showEditProductModal = true,
                showCreateProductModal = false,
                castCreateBusy = false,
                editProductLoading = true,
                error = null,
                editProductForm = EditProductForm(
                    productId = normalizeAdminUuid(row.productId),
                    productLineId = lineIdN,
                    catalogSectionId = sectionIdN,
                    name = row.productName,
                    nameUr = row.productNameUr.orEmpty(),
                    imageUrl = row.imageUrl?.trim()?.takeIf { it.isNotEmpty() },
                    pendingImageFile = null,
                ),
                catalogSectionsForCreate = emptyList()
            )
            val linesResult = runCatching {
                val lines = api.getProductLines().map {
                    ProductLineDto(id = normalizeAdminUuid(it.id), name = it.name)
                }
                val sectionsRaw = api.getCatalogSections(lineIdN).map {
                    CatalogSectionDto(
                        id = normalizeAdminUuid(it.id),
                        code = it.code,
                        name = it.name
                    )
                }
                var sections = sectionsRaw
                if (sectionIdN.isNotBlank() && sections.none { it.id == sectionIdN }) {
                    val label = row.sectionName.trim().takeIf { it.isNotEmpty() && it != "-" }
                    sections = sections + CatalogSectionDto(
                        id = sectionIdN,
                        code = "—",
                        name = label
                    )
                }
                lines to sections
            }
            val sizesResult = runCatching { api.getPickerValues("size") }
            val mechsResult = runCatching { api.getPickerValues("mechanism") }
            val gradesResult = runCatching { pickerSuggestions("Grade") }
            val wheelsResult = runCatching { pickerSuggestions("Wheel Size") }
            val brandsResult = runCatching { api.getBrandOptions() }
            val castsResult = runCatching { api.getCastOptions(lineIdN) }
            val modsResult = runCatching { api.getProductModifiers(row.productId) }
            if (linesResult.isFailure) {
                _uiState.value = _uiState.value.copy(
                    editProductLoading = false,
                    error = linesResult.exceptionOrNull()?.message ?: "Failed to load edit form"
                )
            } else {
                val (lines, sections) = linesResult.getOrThrow()
                val mods = modsResult.getOrDefault(ProductModifiersDto())
                val f = _uiState.value.editProductForm
                _uiState.value = _uiState.value.copy(
                    editProductLoading = false,
                    productLinesOptions = lines,
                    catalogSectionsForCreate = sections,
                    sizePickerOptions = sizesResult.getOrDefault(emptyList()),
                    mechanismPickerOptions = mechsResult.getOrDefault(emptyList()),
                    gradePickerOptions = gradesResult.getOrDefault(emptyList()),
                    wheelPickerOptions = wheelsResult.getOrDefault(emptyList()),
                    brandOptionsForCreate = brandsResult.getOrDefault(emptyList()),
                    castOptionsForCreate = castsResult.getOrDefault(emptyList()),
                    editProductForm = f.copy(
                        selectedSizes = mods.sizes,
                        selectedMechanisms = mods.mechanisms,
                        selectedGrades = mods.grades,
                        selectedWheelSizes = mods.wheelSizes,
                        selectedBrandCodes = mods.brandCodes,
                        selectedCastIds = mods.castIds.map { normalizeAdminUuid(it) },
                    ),
                )
            }
        }
    }

    fun setEditProductLine(lineId: String) {
        val normalized = normalizeAdminUuid(lineId)
        val f = _uiState.value.editProductForm
        if (normalized == f.productLineId) return
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(productLineId = normalized, catalogSectionId = "")
        )
        if (normalized.isBlank()) {
            _uiState.value = _uiState.value.copy(catalogSectionsForCreate = emptyList())
            return
        }
        scope.launch {
            runCatching { api.getCatalogSections(normalized) }
                .onSuccess { raw ->
                    val sections = raw.map {
                        CatalogSectionDto(
                            id = normalizeAdminUuid(it.id),
                            code = it.code,
                            name = it.name
                        )
                    }
                    _uiState.value = _uiState.value.copy(catalogSectionsForCreate = sections)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(error = e.message ?: "Failed to load sections")
                }
        }
    }

    fun updateEditProductForm(
        name: String? = null,
        nameUr: String? = null,
        catalogSectionId: String? = null,
    ) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(
                name = name ?: f.name,
                nameUr = nameUr ?: f.nameUr,
                catalogSectionId = when {
                    catalogSectionId != null -> normalizeAdminUuid(catalogSectionId)
                    else -> f.catalogSectionId
                },
            )
        )
    }

    // ── Edit product modifier add/remove helpers ──────────────────────────────

    fun addEditProductSize(value: String) {
        val v = value.trim(); if (v.isBlank()) return
        val f = _uiState.value.editProductForm
        if (v in f.selectedSizes) return
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(selectedSizes = f.selectedSizes + v),
            sizePickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.sizePickerOptions, v),
        )
        registerPickerModifierAsync("size", v)
    }

    fun removeEditProductSize(value: String) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(editProductForm = f.copy(selectedSizes = f.selectedSizes - value))
    }

    fun addEditProductMechanism(value: String) {
        val v = value.trim(); if (v.isBlank()) return
        val f = _uiState.value.editProductForm
        if (v in f.selectedMechanisms) return
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(selectedMechanisms = f.selectedMechanisms + v),
            mechanismPickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.mechanismPickerOptions, v),
        )
        registerPickerModifierAsync("mechanism", v)
    }

    fun removeEditProductMechanism(value: String) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(editProductForm = f.copy(selectedMechanisms = f.selectedMechanisms - value))
    }

    fun addEditProductGrade(value: String) {
        val v = value.trim(); if (v.isBlank()) return
        val f = _uiState.value.editProductForm
        if (v in f.selectedGrades) return
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(selectedGrades = f.selectedGrades + v),
            gradePickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.gradePickerOptions, v),
        )
        registerPickerModifierAsync("grade", v)
    }

    fun removeEditProductGrade(value: String) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(editProductForm = f.copy(selectedGrades = f.selectedGrades - value))
    }

    fun addEditProductWheel(value: String) {
        val v = value.trim(); if (v.isBlank()) return
        val f = _uiState.value.editProductForm
        if (v in f.selectedWheelSizes) return
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(selectedWheelSizes = f.selectedWheelSizes + v),
            wheelPickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.wheelPickerOptions, v),
        )
        registerPickerModifierAsync("wheel_size", v)
    }

    fun removeEditProductWheel(value: String) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(editProductForm = f.copy(selectedWheelSizes = f.selectedWheelSizes - value))
    }

    fun addEditProductBrand(value: String) {
        val v = value.trim(); if (v.isBlank()) return
        val f = _uiState.value.editProductForm
        if (f.selectedBrandCodes.any { it.equals(v, ignoreCase = true) }) return
        _uiState.value = _uiState.value.copy(editProductForm = f.copy(selectedBrandCodes = f.selectedBrandCodes + v))
    }

    fun removeEditProductBrand(value: String) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(selectedBrandCodes = f.selectedBrandCodes.filterNot { it.equals(value, ignoreCase = true) })
        )
    }

    fun addEditProductCast(value: String) {
        val v = normalizeAdminUuid(value.trim()); if (v.isBlank()) return
        val f = _uiState.value.editProductForm
        if (f.selectedCastIds.any { it.equals(v, ignoreCase = true) }) return
        _uiState.value = _uiState.value.copy(editProductForm = f.copy(selectedCastIds = f.selectedCastIds + v))
    }

    fun removeEditProductCast(value: String) {
        val v = normalizeAdminUuid(value.trim())
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(selectedCastIds = f.selectedCastIds.filterNot { it.equals(v, ignoreCase = true) })
        )
    }

    fun setCreateProductImageFile(file: File?) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(pendingImageFile = file),
        )
    }

    fun setEditProductImageFile(file: File?) {
        val f = _uiState.value.editProductForm
        _uiState.value = _uiState.value.copy(
            editProductForm = f.copy(
                pendingImageFile = file,
                clearImage = file == null && !f.imageUrl.isNullOrBlank(),
            ),
        )
    }

    fun saveEditedProduct() {
        val f = _uiState.value.editProductForm
        if (f.productId.isBlank() || f.productLineId.isBlank() || f.catalogSectionId.isBlank() || f.name.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Product line, section, and English name are required")
            return
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching {
                api.updateProduct(
                    UpdateProductRequestDto(
                        productId = f.productId,
                        productLineId = f.productLineId,
                        catalogSectionId = f.catalogSectionId,
                        name = f.name.trim(),
                        nameUr = f.nameUr.trim().ifBlank { null },
                        castIds = f.selectedCastIds.map { normalizeAdminUuid(it) },
                        brandCodes = f.selectedBrandCodes,
                        defaultSizes = f.selectedSizes,
                        defaultMechanisms = f.selectedMechanisms,
                        defaultWheelSizes = f.selectedWheelSizes,
                        defaultGrades = f.selectedGrades,
                    )
                )
                when {
                    f.pendingImageFile != null -> api.uploadProductImage(f.productId, f.pendingImageFile!!)
                    f.clearImage -> api.deleteProductImage(f.productId)
                }
            }.onSuccess {
                addLog("Updated product ${f.name.trim()}")
                _uiState.value = _uiState.value.copy(
                    saving = false,
                    showEditProductModal = false,
                    editProductForm = EditProductForm(),
                    catalogSectionsForCreate = emptyList()
                )
                loadProductsPage(_uiState.value.productsPage)
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Update product failed")
            }
        }
    }

    fun setCreateProductLine(lineId: String) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(
                productLineId = lineId,
                catalogSectionId = "",
                selectedCastIds = emptyList(),
            )
        )
        if (lineId.isBlank()) {
            _uiState.value = _uiState.value.copy(
                catalogSectionsForCreate = emptyList(),
                castOptionsForCreate = emptyList(),
            )
            return
        }
        scope.launch {
            runCatching { api.getCatalogSections(lineId) }
                .onSuccess { sections ->
                    _uiState.value = _uiState.value.copy(catalogSectionsForCreate = sections)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(error = e.message ?: "Failed to load sections")
                }
            runCatching {
                val casts = api.getCastOptions(lineId)
                val modifierCastCodes =
                    api.getPickerValues("Cast", modifiersOnly = true).map { it.trim() }.filter { it.isNotEmpty() }.toSet()
                val filtered =
                    if (modifierCastCodes.isEmpty()) casts
                    else casts.filter { row -> modifierCastCodes.any { mc -> mc.equals(row.code, ignoreCase = true) } }
                val grades = pickerSuggestions("Grade")
                val wheels = pickerSuggestions("Wheel Size")
                _uiState.value = _uiState.value.copy(
                    castOptionsForCreate = filtered,
                    gradePickerOptions = grades,
                    wheelPickerOptions = wheels,
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(error = e.message ?: "Failed to load casts")
            }
        }
    }

    fun updateCreateProductForm(
        productLineId: String? = null,
        catalogSectionId: String? = null,
        name: String? = null,
        nameUr: String? = null,
        basePrice: String? = null,
        costPrice: String? = null,
    ) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(
                productLineId = productLineId ?: f.productLineId,
                catalogSectionId = catalogSectionId ?: f.catalogSectionId,
                name = name ?: f.name,
                nameUr = nameUr ?: f.nameUr,
                basePrice = basePrice ?: f.basePrice,
                costPrice = costPrice ?: f.costPrice,
            )
        )
    }

    fun addCreateProductSize(value: String) {
        val v = value.trim()
        if (v.isBlank()) return
        val f = _uiState.value.createProductForm
        if (v in f.selectedSizes) return
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedSizes = f.selectedSizes + v),
            sizePickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.sizePickerOptions, v),
        )
        registerPickerModifierAsync("size", v)
    }

    fun removeCreateProductSize(value: String) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedSizes = f.selectedSizes - value)
        )
    }

    fun addCreateProductMechanism(value: String) {
        val v = value.trim()
        if (v.isBlank()) return
        val f = _uiState.value.createProductForm
        if (v in f.selectedMechanisms) return
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedMechanisms = f.selectedMechanisms + v),
            mechanismPickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.mechanismPickerOptions, v),
        )
        registerPickerModifierAsync("mechanism", v)
    }

    fun removeCreateProductMechanism(value: String) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedMechanisms = f.selectedMechanisms - value)
        )
    }

    fun addCreateProductCast(value: String) {
        val v = normalizeAdminUuid(value.trim())
        if (v.isBlank()) return
        val f = _uiState.value.createProductForm
        if (f.selectedCastIds.any { it.equals(v, ignoreCase = true) }) return
        val code =
            _uiState.value.castOptionsForCreate.firstOrNull { it.id.equals(v, ignoreCase = true) }?.code?.trim().orEmpty()
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedCastIds = f.selectedCastIds + v),
        )
        if (code.isNotBlank()) registerPickerModifierAsync("cast", code)
    }

    fun removeCreateProductCast(value: String) {
        val v = normalizeAdminUuid(value.trim())
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedCastIds = f.selectedCastIds.filterNot { it.equals(v, ignoreCase = true) })
        )
    }

    fun createCastForCurrentProductLine(code: String, name: String, nameUr: String?) {
        val lineId = normalizeAdminUuid(_uiState.value.createProductForm.productLineId.trim())
        if (lineId.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Select a product line before creating a cast")
            return
        }
        val c = code.trim()
        val n = name.trim()
        if (c.isEmpty() || n.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Cast code and English name are required")
            return
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(castCreateBusy = true, error = null)
            runCatching {
                api.createCast(
                    CreateCastRequestDto(
                        productLineId = lineId,
                        code = c,
                        name = n,
                        nameUr = nameUr?.trim()?.ifBlank { null },
                    )
                )
            }.onSuccess { dto ->
                val merged = (_uiState.value.castOptionsForCreate + dto).distinctBy { it.id }
                val f = _uiState.value.createProductForm
                val picked = dto.id.trim()
                val nextCasts =
                    if (picked.isEmpty() || f.selectedCastIds.any { it.equals(picked, ignoreCase = true) }) {
                        f.selectedCastIds
                    } else {
                        f.selectedCastIds + picked
                    }
                _uiState.value = _uiState.value.copy(
                    castCreateBusy = false,
                    castOptionsForCreate = merged,
                    createProductForm = f.copy(selectedCastIds = nextCasts),
                    error = null,
                    inlineCastFormNonce = _uiState.value.inlineCastFormNonce + 1,
                )
            }.onFailure { e ->
                _uiState.value = _uiState.value.copy(
                    castCreateBusy = false,
                    error = e.message ?: "Create cast failed"
                )
            }
        }
    }

    fun addCreateProductBrand(value: String) {
        val v = value.trim()
        if (v.isBlank()) return
        val f = _uiState.value.createProductForm
        if (f.selectedBrandCodes.any { it.equals(v, ignoreCase = true) }) return
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedBrandCodes = f.selectedBrandCodes + v)
        )
    }

    fun removeCreateProductBrand(value: String) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedBrandCodes = f.selectedBrandCodes.filterNot { it.equals(value, ignoreCase = true) })
        )
    }

    fun addCreateProductWheel(value: String) {
        val v = value.trim()
        if (v.isBlank()) return
        val f = _uiState.value.createProductForm
        if (v in f.selectedWheelSizes) return
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedWheelSizes = f.selectedWheelSizes + v),
            wheelPickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.wheelPickerOptions, v),
        )
        registerPickerModifierAsync("wheel_size", v)
    }

    fun removeCreateProductWheel(value: String) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedWheelSizes = f.selectedWheelSizes - value)
        )
    }

    fun addCreateProductGrade(value: String) {
        val v = value.trim()
        if (v.isBlank()) return
        val f = _uiState.value.createProductForm
        if (v in f.selectedGrades) return
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedGrades = f.selectedGrades + v),
            gradePickerOptions = mergeDistinctSortedPickerOptions(_uiState.value.gradePickerOptions, v),
        )
        registerPickerModifierAsync("grade", v)
    }

    fun removeCreateProductGrade(value: String) {
        val f = _uiState.value.createProductForm
        _uiState.value = _uiState.value.copy(
            createProductForm = f.copy(selectedGrades = f.selectedGrades - value)
        )
    }

    fun createProduct() {
        val f = _uiState.value.createProductForm
        if (f.productLineId.isBlank() || f.catalogSectionId.isBlank() || f.name.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "productLineId, catalogSectionId and name are required")
            return
        }
        if (!isCreateBrandOptionalForSection(f, _uiState.value.catalogSectionsForCreate) && f.selectedBrandCodes.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Select at least one brand for this catalog section")
            return
        }
        if (!isCreateCastOptionalForSection(f, _uiState.value.catalogSectionsForCreate) && f.selectedCastIds.isEmpty()) {
            _uiState.value = _uiState.value.copy(error = "Select at least one cast for the product line")
            return
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val sizes = f.selectedSizes
            val mechs = f.selectedMechanisms
            var imageUploadError: String? = null
            val created = runCatching {
                api.createProduct(
                    CreateProductRequestDto(
                        productLineId = f.productLineId,
                        catalogSectionId = f.catalogSectionId,
                        name = f.name,
                        nameUr = f.nameUr.trim().ifBlank { null },
                        castIds = f.selectedCastIds.map { normalizeAdminUuid(it) },
                        brandCodes = f.selectedBrandCodes,
                        defaultSizes = sizes,
                        defaultMechanisms = mechs,
                        defaultWheelSizes = f.selectedWheelSizes,
                        defaultGrades = f.selectedGrades,
                        basePrice = f.basePrice.trim().ifBlank { null },
                        costPrice = f.costPrice.trim().ifBlank { null },
                    )
                )
            }.getOrElse { e ->
                _uiState.value = _uiState.value.copy(
                    saving = false,
                    error = e.message ?: "Create product failed",
                )
                return@launch
            }
            val pending = f.pendingImageFile
            if (pending != null) {
                runCatching { api.uploadProductImage(created.productId, pending) }
                    .onFailure { e ->
                        imageUploadError = e.message ?: "Image upload failed"
                    }
            }
            addLog("Created product ${f.name}")
            _uiState.value = _uiState.value.copy(
                saving = false,
                showCreateProductModal = false,
                castCreateBusy = false,
                createProductForm = CreateProductForm(),
                error = imageUploadError?.let {
                    "Product created, but image upload failed: $it. Redeploy the API, then edit the product to upload the image."
                },
            )
            refreshAll()
        }
    }

    fun openVariants(productId: String) {
        scope.launch {
            val prev = _uiState.value
            val sameProduct = prev.selectedProductId == productId
            _uiState.value = prev.copy(
                selectedProductId = productId,
                loading = true,
                error = null,
                variantPage = if (sameProduct) prev.variantPage else 1,
                variants = if (sameProduct) prev.variants else emptyList()
            )
            runCatching { api.getVariants(productId) }
                .onSuccess { variants ->
                    _uiState.value = _uiState.value.copy(loading = false, variants = variants)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(loading = false, error = e.message ?: "Failed loading variants")
                }
        }
    }

    fun closeVariants() {
        _uiState.value = _uiState.value.copy(selectedProductId = null, variants = emptyList(), variantPage = 1)
    }

    fun archiveProduct(productId: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.archiveProduct(productId) }
                .onSuccess {
                    addLog("Archived product $productId")
                    _uiState.value = _uiState.value.copy(saving = false)
                    loadProductsPage(_uiState.value.productsPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Archive failed")
                }
        }
    }

    fun activateProduct(productId: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.activateProduct(productId) }
                .onSuccess {
                    addLog("Activated product $productId")
                    _uiState.value = _uiState.value.copy(saving = false)
                    loadProductsPage(_uiState.value.productsPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Activate failed")
                }
        }
    }

    fun patchVariant(variantId: String, legacyId: String?, stock: Int?, nameUr: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.patchVariant(variantId, PatchVariantRequestDto(legacyId = legacyId, stock = stock, nameUr = nameUr)) }
                .onSuccess {
                    addLog("Patched variant $variantId")
                    _uiState.value = _uiState.value.copy(saving = false)
                    _uiState.value.selectedProductId?.let { openVariants(it) }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Variant patch failed")
                }
        }
    }

    fun deleteVariant(variantId: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.deleteVariant(variantId) }
                .onSuccess {
                    addLog("Deleted variant $variantId")
                    _uiState.value = _uiState.value.copy(saving = false)
                    _uiState.value.selectedProductId?.let { openVariants(it) }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Variant delete failed")
                }
        }
    }

    fun setVariantActive(variantId: String, isActive: Boolean) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.patchVariant(variantId, PatchVariantRequestDto(isActive = isActive)) }
                .onSuccess {
                    addLog(if (isActive) "Activated variant $variantId" else "Inactivated variant $variantId")
                    _uiState.value = _uiState.value.copy(saving = false)
                    _uiState.value.selectedProductId?.let { openVariants(it) }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Variant status update failed")
                }
        }
    }

    private fun matrixCellDraftValue(v: String?): String =
        when {
            v == null || v == "0" || v == "0.00" -> ""
            else -> v
        }

    fun openMatrixRowEditor(row: PricingMatrixRowDto) {
        _uiState.value = _uiState.value.copy(
            matrixEditDraft = MatrixRowEditDraft(
                productId = row.productId,
                productName = row.productName,
                productNameUr = row.productNameUr,
                baseSku = row.baseSku,
                st = matrixCellDraftValue(row.st),
                c = matrixCellDraftValue(row.c),
                d = matrixCellDraftValue(row.d),
                gen = matrixCellDraftValue(row.gen),
                n = matrixCellDraftValue(row.n),
                s = matrixCellDraftValue(row.s),
                t = matrixCellDraftValue(row.t)
            ),
            error = null
        )
    }

    fun closeMatrixRowEditor() {
        _uiState.value = _uiState.value.copy(matrixEditDraft = null)
    }

    fun updateMatrixEditDraft(
        st: String? = null,
        c: String? = null,
        brandD: String? = null,
        gen: String? = null,
        n: String? = null,
        s: String? = null,
        t: String? = null
    ) {
        val draft = _uiState.value.matrixEditDraft ?: return
        _uiState.value = _uiState.value.copy(
            matrixEditDraft = draft.copy(
                st = st ?: draft.st,
                c = c ?: draft.c,
                d = brandD ?: draft.d,
                gen = gen ?: draft.gen,
                n = n ?: draft.n,
                s = s ?: draft.s,
                t = t ?: draft.t
            )
        )
    }

    fun saveMatrixRowEditor() {
        val d = _uiState.value.matrixEditDraft ?: return
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            val cells = mutableListOf<UpdateMatrixCellDto>()
            val pairs = listOf(
                "ST" to d.st,
                "C" to d.c,
                "D" to d.d,
                "GEN" to d.gen,
                "N" to d.n,
                "S" to d.s,
                "T" to d.t
            )
            for ((code, raw) in pairs) {
                val t = raw.trim()
                if (t.isEmpty()) continue
                val num = t.toDoubleOrNull()
                if (num == null) {
                    _uiState.value = _uiState.value.copy(saving = false, error = "Invalid price for $code: \"$t\"")
                    return@launch
                }
                cells += UpdateMatrixCellDto(
                    productId = d.productId,
                    brandCode = code,
                    basePrice = t,
                    allowZeroOverride = num <= 0.0
                )
            }
            if (cells.isEmpty()) {
                _uiState.value = _uiState.value.copy(saving = false, matrixEditDraft = null)
                return@launch
            }
            runCatching { api.updateMatrix(cells) }
                .onSuccess {
                    addLog("Saved pricing row: ${d.productName}")
                    _uiState.value = _uiState.value.copy(saving = false, matrixEditDraft = null)
                    val p = _uiState.value.matrixPage
                    runCatching { api.getMatrixPage(p, _uiState.value.matrixPageSize) }
                        .onSuccess { m ->
                            _uiState.value = _uiState.value.copy(matrix = m.items, matrixTotal = m.total, matrixPage = m.page)
                        }
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(
                        saving = false,
                        error = e.message ?: "Matrix update failed"
                    )
                }
        }
    }

    fun updateMatrixCell(productId: String, brandCode: String, value: String) {
        val updated = _uiState.value.matrix.map { row ->
            if (row.productId != productId) row else when (brandCode) {
                "ST" -> row.copy(st = value)
                "C" -> row.copy(c = value)
                "D" -> row.copy(d = value)
                "GEN" -> row.copy(gen = value)
                "N" -> row.copy(n = value)
                "S" -> row.copy(s = value)
                else -> row.copy(t = value)
            }
        }
        _uiState.value = _uiState.value.copy(matrix = updated)
        publishMatrixChanges(listOf(UpdateMatrixCellDto(productId, brandCode, value)))
    }

    fun setBulkBrand(brandCode: String) {
        _uiState.value = _uiState.value.copy(
            bulkAdjustment = _uiState.value.bulkAdjustment.copy(brandCode = brandCode)
        )
    }

    fun setBulkType(type: AdjustmentType) {
        _uiState.value = _uiState.value.copy(
            bulkAdjustment = _uiState.value.bulkAdjustment.copy(type = type)
        )
    }

    fun setBulkAmount(amount: String) {
        _uiState.value = _uiState.value.copy(
            bulkAdjustment = _uiState.value.bulkAdjustment.copy(amount = amount)
        )
    }

    fun applyBulkAdjustment(brandId: String, type: AdjustmentType, amount: String) {
        val delta = amount.toDoubleOrNull() ?: return
        val pending = mutableListOf<UpdateMatrixCellDto>()
        val next = _uiState.value.matrix.map { row ->
            fun currentOf(code: String): String? = when (code) {
                "ST" -> row.st
                "C" -> row.c
                "D" -> row.d
                "GEN" -> row.gen
                "N" -> row.n
                "S" -> row.s
                else -> row.t
            }

            fun updateRow(v: String): PricingMatrixRowDto = when (brandId) {
                "ST" -> row.copy(st = v)
                "C" -> row.copy(c = v)
                "D" -> row.copy(d = v)
                "GEN" -> row.copy(gen = v)
                "N" -> row.copy(n = v)
                "S" -> row.copy(s = v)
                else -> row.copy(t = v)
            }

            val current = currentOf(brandId)?.toDoubleOrNull()
            if (current == null) return@map row
            val result = when (type) {
                AdjustmentType.Absolute -> current + delta
                AdjustmentType.Percentage -> current + (current * delta / 100.0)
            }
            val normalized = formatPrice(result)
            pending += UpdateMatrixCellDto(row.productId, brandId, normalized)
            updateRow(normalized)
        }

        _uiState.value = _uiState.value.copy(matrix = next)
        publishMatrixChanges(pending)
        addLog("Applied bulk ${type.name} $amount on $brandId (current page)")
    }

    private fun publishMatrixChanges(cells: List<UpdateMatrixCellDto>) {
        if (cells.isEmpty()) return
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.updateMatrix(cells) }
                .onSuccess {
                    addLog("Updated ${cells.size} matrix cells")
                    _uiState.value = _uiState.value.copy(saving = false)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Matrix update failed")
                }
        }
    }

    fun updateCreateModifierForm(attrKey: String? = null, attrValue: String? = null, attrValueUr: String? = null, priceImpact: String? = null) {
        val f = _uiState.value.createModifierForm
        _uiState.value = _uiState.value.copy(
            createModifierForm = f.copy(
                attrKey = attrKey ?: f.attrKey,
                attrValue = attrValue ?: f.attrValue,
                attrValueUr = attrValueUr ?: f.attrValueUr,
                priceImpact = priceImpact ?: f.priceImpact
            )
        )
    }

    fun createModifier() {
        val f = _uiState.value.createModifierForm
        if (f.attrKey.isBlank() || f.attrValue.isBlank() || f.priceImpact.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "attrKey, attrValue and priceImpact are required")
            return
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.createModifier(CreateModifierRequestDto(f.attrKey, f.attrValue, f.priceImpact, f.attrValueUr)) }
                .onSuccess {
                    addLog("Created modifier ${f.attrKey}:${f.attrValue}")
                    _uiState.value = _uiState.value.copy(saving = false, createModifierForm = ModifierForm())
                    loadModifiersPage(_uiState.value.modifiersPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Create modifier failed")
                }
        }
    }

    fun updateUpdateModifierForm(
        modifierId: String? = null,
        attrKey: String? = null,
        attrValue: String? = null,
        attrValueUr: String? = null,
        priceImpact: String? = null,
    ) {
        val f = _uiState.value.updateModifierForm
        _uiState.value = _uiState.value.copy(
            updateModifierForm = f.copy(
                modifierId = modifierId ?: f.modifierId,
                attrKey = attrKey ?: f.attrKey,
                attrValue = attrValue ?: f.attrValue,
                attrValueUr = attrValueUr ?: f.attrValueUr,
                priceImpact = priceImpact ?: f.priceImpact
            )
        )
    }

    fun openModifierForEdit(row: ModifierRowDto) {
        _uiState.value = _uiState.value.copy(
            editingModifierId = row.id,
            error = null,
            updateModifierForm = UpdateModifierForm(
                modifierId = row.id,
                attrKey = row.attrKey,
                attrValue = row.attrValue,
                attrValueUr = row.attrValueUr?.trim().orEmpty(),
                priceImpact = row.priceImpact,
            )
        )
        kotlinx.browser.window.setTimeout({
            kotlinx.browser.document.getElementById("modifier-edit-panel")
                ?.unsafeCast<org.w3c.dom.HTMLElement>()
                ?.scrollIntoView()
        }, 50)
    }

    fun cancelModifierEdit() {
        _uiState.value = _uiState.value.copy(editingModifierId = null, updateModifierForm = UpdateModifierForm())
    }

    fun patchModifier() {
        val f = _uiState.value.updateModifierForm
        if (f.modifierId.isBlank() || f.attrKey.isBlank() || f.attrValue.isBlank() || f.priceImpact.isBlank()) {
            _uiState.value = _uiState.value.copy(
                error = "modifierId, attribute key, value (English), and price impact are required"
            )
            return
        }
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching {
                api.patchModifier(
                    f.modifierId,
                    PatchModifierRequestDto(
                        priceImpact = f.priceImpact,
                        attrValueUr = f.attrValueUr.trim().takeIf { it.isNotEmpty() },
                        attrKey = f.attrKey,
                        attrValue = f.attrValue,
                    )
                )
            }
                .onSuccess {
                    addLog("Patched modifier ${f.attrKey}:${f.attrValue} (${f.modifierId})")
                    _uiState.value = _uiState.value.copy(
                        saving = false,
                        editingModifierId = null,
                        updateModifierForm = UpdateModifierForm()
                    )
                    loadModifiersPage(_uiState.value.modifiersPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Patch modifier failed")
                }
        }
    }

    fun deleteModifier(modifierId: String) {
        scope.launch {
            _uiState.value = _uiState.value.copy(saving = true, error = null)
            runCatching { api.deleteModifier(modifierId) }
                .onSuccess {
                    addLog("Deleted modifier $modifierId")
                    val s = _uiState.value
                    val clearedEdit =
                        if (s.editingModifierId?.equals(modifierId, ignoreCase = true) == true) null else s.editingModifierId
                    val clearedForm =
                        if (s.updateModifierForm.modifierId.equals(modifierId, ignoreCase = true)) {
                            UpdateModifierForm()
                        } else {
                            s.updateModifierForm
                        }
                    _uiState.value = s.copy(
                        saving = false,
                        editingModifierId = clearedEdit,
                        updateModifierForm = clearedForm
                    )
                    loadModifiersPage(s.modifiersPage)
                }
                .onFailure { e ->
                    _uiState.value = _uiState.value.copy(saving = false, error = e.message ?: "Delete modifier failed")
                }
        }
    }

    private fun addLog(message: String) {
        _uiState.value = _uiState.value.copy(
            auditLog = listOf(message) + _uiState.value.auditLog.take(49)
        )
    }
}

private fun formatPrice(value: Double): String = (kotlin.math.round(value * 100.0) / 100.0).toString()
